import { Button } from "@9amhealth/shared";
import type { FC } from "react";
import React from "react";
import { DbFileService } from "src/lib/db/DbFileService";

const formatBytes = (bytes: number): string => {
  if (bytes === 0) {
    return "0 B";
  }
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const DebugFileCache: FC = () => {
  const [stats, setStats] = React.useState<{
    count: number;
    totalSize: number;
  } | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lastAction, setLastAction] = React.useState<string | null>(null);

  const loadStats = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cacheStats = await DbFileService.getCacheStats();
      setStats(cacheStats);
      setLastAction(`Loaded stats at ${new Date().toLocaleTimeString()}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadStats();
  }, [loadStats]);

  const clearAllCache = async () => {
    setLoading(true);
    try {
      await DbFileService.clearAll();
      setLastAction(`Cleared all cache at ${new Date().toLocaleTimeString()}`);
      await loadStats();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear all cache");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: "10px" }}>
        <Button
          size="medium"
          onPress={() => void loadStats()}
          isDisabled={loading}
        >
          Refresh Stats
        </Button>
        <Button
          size="medium"
          onPress={() => void clearAllCache()}
          isDisabled={loading}
        >
          Clear All Cache
        </Button>
      </div>

      {error && (
        <div
          style={{
            padding: "8px",
            backgroundColor: "#ffebee",
            borderRadius: "4px",
            color: "#c62828",
            marginBottom: "10px"
          }}
        >
          Error: {error}
        </div>
      )}

      <div style={{ marginTop: "10px" }}>
        {stats && (
          <div
            style={{
              marginTop: "10px",
              padding: "8px",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px"
            }}
          >
            <p>
              <strong>Cache Stats:</strong>
            </p>
            <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
              <li>
                Files cached: <strong>{stats.count}</strong>
              </li>
              <li>
                Total size: <strong>{formatBytes(stats.totalSize)}</strong>
              </li>
            </ul>
          </div>
        )}

        {lastAction && (
          <p style={{ marginTop: "10px", fontSize: "9px", color: "#666" }}>
            Last action: {lastAction}
          </p>
        )}
      </div>

      <div
        style={{
          marginTop: "10px",
          padding: "8px",
          backgroundColor: "#e3f2fd",
          borderRadius: "4px",
          fontSize: "9px"
        }}
      >
        <p>
          <strong>Config:</strong>
        </p>
        <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
          <li>Max age: 30 days</li>
          <li>Max size: {formatBytes(100 * 1024 * 1024)}</li>
          <li>Storage: Dexie IndexedDB</li>
        </ul>
      </div>
    </div>
  );
};

export default DebugFileCache;
